<template>
  <transition name="filter-roles-modal" appear>
    <div
      v-if="value"
      class="filter-roles-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-roles-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-roles-modal__card">
        <div class="filter-roles-modal__header">
          <span id="filter-roles-modal-title" class="filter-roles-modal__title">
            Filter roles
          </span>
          <button
            type="button"
            class="filter-roles-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-roles-modal__filters-row">
          <div class="filter-roles-modal__column">
            <span class="filter-roles-modal__column-title">Status</span>
            <div class="filter-roles-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div class="filter-roles-modal__column filter-roles-modal__column--radio">
            <RadioInput
              label="Archived"
              :availableItems="archivedOptions"
              :checkedItem="archivedCheckedItem"
              @changeradio="onArchivedChange"
            />
          </div>

          <div class="filter-roles-modal__column filter-roles-modal__column--radio">
            <RadioInput
              label="Validity"
              :availableItems="validityOptions"
              :checkedItem="validityCheckedItem"
              @changeradio="onValidityChange"
            />
          </div>
        </div>

        <div class="filter-roles-modal__divider" />

        <div class="filter-roles-modal__filters-row">
          <div class="filter-roles-modal__column filter-roles-modal__column--radio">
            <RadioInput
              label="Positions"
              :availableItems="positionsOptions"
              :checkedItem="positionsCheckedItem"
              @changeradio="onPositionsChange"
            />
          </div>

          <div class="filter-roles-modal__column filter-roles-modal__column--radio">
            <RadioInput
              label="In-use"
              :availableItems="inUseOptions"
              :checkedItem="inUseCheckedItem"
              @changeradio="onInUseChange"
            />
          </div>

          <div class="filter-roles-modal__column filter-roles-modal__column--radio">
            <RadioInput
              label="Timeline"
              :availableItems="timelineOptions"
              :checkedItem="timelineCheckedItem"
              @changeradio="onTimelineChange"
            />
          </div>
        </div>

        <div class="filter-roles-modal__footer">
          <button type="button" class="filter-roles-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import {
  DEFAULT_ROLE_FILTER_PAYLOAD,
  ARCHIVED_LABEL_BY_VALUE,
  VALIDITY_LABEL_BY_VALUE,
  POSITIONS_LABEL_BY_VALUE,
  IN_USE_LABEL_BY_VALUE,
  TIMELINE_LABEL_BY_VALUE,
} from "./roleFilterPayload";

const props = defineProps({
  value: { type: Boolean, default: false },
  initialStatuses: {
    type: Object,
    default: () => ({ draft: true, active: true, retired: false }),
  },
  initialArchived: {
    type: String,
    default: DEFAULT_ROLE_FILTER_PAYLOAD.archived,
  },
  initialValidity: {
    type: String,
    default: DEFAULT_ROLE_FILTER_PAYLOAD.validity,
  },
  initialPositions: {
    type: String,
    default: DEFAULT_ROLE_FILTER_PAYLOAD.positions,
  },
  initialInUse: {
    type: String,
    default: DEFAULT_ROLE_FILTER_PAYLOAD.inUse,
  },
  initialTimeline: {
    type: String,
    default: DEFAULT_ROLE_FILTER_PAYLOAD.timeline,
  },
});

const emit = defineEmits(["input", "close", "apply", "reset"]);

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Retired", value: "retired" },
];

const NO_FILTER_OPTION = { label: "No Filter" };

const archivedOptions = [
  { label: "Do Not Show" },
  { label: "Show" },
];

const validityOptions = [
  NO_FILTER_OPTION,
  { label: "Is valid" },
  { label: "Is not valid" },
];

const positionsOptions = [
  NO_FILTER_OPTION,
  { label: "Attached" },
  { label: "No position attached" },
];

const inUseOptions = [
  NO_FILTER_OPTION,
  { label: "In use" },
  { label: "Not used" },
];

const timelineOptions = [
  NO_FILTER_OPTION,
  { label: "Recently Activated" },
  { label: "Recently Retired" },
  { label: "Recently Created" },
];

const statuses = ref({ ...props.initialStatuses });
const archived = ref(props.initialArchived);
const validity = ref(props.initialValidity);
const positions = ref(props.initialPositions);
const inUse = ref(props.initialInUse);
const timeline = ref(props.initialTimeline);

const archivedCheckedItem = computed(
  () => ARCHIVED_LABEL_BY_VALUE[archived.value] || NO_FILTER_OPTION.label
);
const validityCheckedItem = computed(
  () => VALIDITY_LABEL_BY_VALUE[validity.value] || NO_FILTER_OPTION.label
);
const positionsCheckedItem = computed(
  () => POSITIONS_LABEL_BY_VALUE[positions.value] || NO_FILTER_OPTION.label
);
const inUseCheckedItem = computed(
  () => IN_USE_LABEL_BY_VALUE[inUse.value] || NO_FILTER_OPTION.label
);
const timelineCheckedItem = computed(
  () => TIMELINE_LABEL_BY_VALUE[timeline.value] || NO_FILTER_OPTION.label
);

watch(
  () => props.value,
  (open) => {
    if (open) syncFromProps();
  },
  { immediate: true }
);

function syncFromProps() {
  statuses.value = { ...props.initialStatuses };
  archived.value = props.initialArchived;
  validity.value = props.initialValidity;
  positions.value = props.initialPositions;
  inUse.value = props.initialInUse;
  timeline.value = props.initialTimeline;
}

function labelToValue(labelMap, label, fallback) {
  const entry = Object.entries(labelMap).find(([, mapped]) => mapped === label);
  return entry ? entry[0] : fallback;
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function onArchivedChange(payload) {
  archived.value = labelToValue(
    ARCHIVED_LABEL_BY_VALUE,
    payload?.value,
    DEFAULT_ROLE_FILTER_PAYLOAD.archived
  );
}

function onValidityChange(payload) {
  validity.value = labelToValue(
    VALIDITY_LABEL_BY_VALUE,
    payload?.value,
    DEFAULT_ROLE_FILTER_PAYLOAD.validity
  );
}

function onPositionsChange(payload) {
  positions.value = labelToValue(
    POSITIONS_LABEL_BY_VALUE,
    payload?.value,
    DEFAULT_ROLE_FILTER_PAYLOAD.positions
  );
}

function onInUseChange(payload) {
  inUse.value = labelToValue(
    IN_USE_LABEL_BY_VALUE,
    payload?.value,
    DEFAULT_ROLE_FILTER_PAYLOAD.inUse
  );
}

function onTimelineChange(payload) {
  timeline.value = labelToValue(
    TIMELINE_LABEL_BY_VALUE,
    payload?.value,
    DEFAULT_ROLE_FILTER_PAYLOAD.timeline
  );
}

function handleReset() {
  statuses.value = { ...DEFAULT_ROLE_FILTER_PAYLOAD.statuses };
  archived.value = DEFAULT_ROLE_FILTER_PAYLOAD.archived;
  validity.value = DEFAULT_ROLE_FILTER_PAYLOAD.validity;
  positions.value = DEFAULT_ROLE_FILTER_PAYLOAD.positions;
  inUse.value = DEFAULT_ROLE_FILTER_PAYLOAD.inUse;
  timeline.value = DEFAULT_ROLE_FILTER_PAYLOAD.timeline;
  emit("reset");
}

function handleApply() {
  emit("apply", {
    statuses: { ...statuses.value },
    archived: archived.value,
    validity: validity.value,
    positions: positions.value,
    inUse: inUse.value,
    timeline: timeline.value,
  });
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.filter-roles-modal-enter-active,
.filter-roles-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-roles-modal-enter-from,
.filter-roles-modal-leave-to {
  opacity: 0;
}

.filter-roles-modal-enter-active .filter-roles-modal__card,
.filter-roles-modal-leave-active .filter-roles-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.filter-roles-modal-enter-from .filter-roles-modal__card,
.filter-roles-modal-leave-to .filter-roles-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-roles-modal-enter-active,
  .filter-roles-modal-leave-active,
  .filter-roles-modal-enter-active .filter-roles-modal__card,
  .filter-roles-modal-leave-active .filter-roles-modal__card {
    transition: none !important;
  }
}

.filter-roles-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-roles-modal__card {
  width: 100%;
  max-width: 1032px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-roles-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-roles-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-roles-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-roles-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-roles-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-roles-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-roles-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-roles-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-roles-modal__options {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
}

.filter-roles-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.filter-roles-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-roles-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-roles-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.filter-roles-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}
</style>
